/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import svg_plus from 'neuroglancer/static/plus.svg';
import { DisplayContext } from 'neuroglancer/display_context';
import { addNewLayer, deleteLayer, LayerListSpecification, makeLayer, ManagedUserLayer, SelectedLayerState } from 'neuroglancer/layer';
import { LinkedViewerNavigationState } from 'neuroglancer/layer_group_viewer';
import { NavigationLinkType } from 'neuroglancer/navigation_state';
import 'neuroglancer/noselect.css';
import { WatchableValueInterface } from 'neuroglancer/trackable_value';
import { DropLayers, registerLayerBarDragLeaveHandler, registerLayerBarDropHandlers, registerLayerDragHandlers } from 'neuroglancer/ui/layer_drag_and_drop';
import { animationFrameDebounce } from 'neuroglancer/util/animation_frame_debounce';
import { Owned, RefCounted } from 'neuroglancer/util/disposable';
import { removeFromParent } from 'neuroglancer/util/dom';
import { preventDrag } from 'neuroglancer/util/drag_and_drop';
import { makeCloseButton } from 'neuroglancer/widget/close_button';
import { makeDeleteButton } from 'neuroglancer/widget/delete_button';
import { makeIcon } from 'neuroglancer/widget/icon';
import { PositionWidget } from 'neuroglancer/widget/position_widget';
import { allToolsButton, viewer } from 'src/main';
import { Tooltip } from '../widget/tooltip';
import './layer_bar.less';
import { LayerVisibilityWidget2, LayerVisibilityWidget3 } from './layer_list_panel';
import svg_down from 'neuroglancer/static/down.svg';
import i18n from '../i18n/i18n';
import { mat4, vec4 } from 'gl-matrix';
import svg_proteins from 'neuroglancer/static/people.svg';
import { _param as local_href_param } from 'src/main';
import { thousandth_formatter } from '../util/comm_fun_hq';
// import i18n from '../i18n/i18n';

class LayerWidget extends RefCounted {
  element = document.createElement('div');
  layerNumberElement = document.createElement('div');
  labelElement = document.createElement('div');
  visibleProgress = document.createElement('div');
  prefetchProgress = document.createElement('div');
  labelElementText = document.createTextNode('');
  valueElement = document.createElement('div');
  listElement = document.createElement('div');
  maxLength: number = 0;
  prevValueText: string = '';
  disabled: boolean = true;

  constructor(public groupLayerMap: Map<string, ManagedUserLayer>, public layer: ManagedUserLayer, public panel: LayerBar, public isRadio:boolean = false) {
    super();
    const {
      element,
      labelElement,
      layerNumberElement,
      valueElement,
      visibleProgress,
      prefetchProgress,
      labelElementText,
      listElement
    } = this;
    element.className = `neuroglancer-layer-item neuroglancer-noselect ${layer.name}`;
    element.id = layer.name;
    element.draggable = false;
    element.style.display = this.layer.genename === 'annotationCellbin' ? 'none' : 'flex';
    element.addEventListener('click', (event: MouseEvent) => {
      event.stopPropagation();
    });
    const {disabledChanged} = this.layer
    disabledChanged.add(() => {
      element.classList[this.layer.disabled ? 'add' : 'remove']('ng-common-disabled')
    })
    if (!this.layer.layer?.hasData || this.layer.disabled) {
      element.classList.add('ng-common-disabled')
      this.layer.setDisabled(true)
    }
    // if (!this.layer.menupath && this.layer.type !== 'image') {
    //   element.classList.add('hide')
    // }

    element.appendChild(visibleProgress);
    element.appendChild(prefetchProgress);
    labelElement.className = 'neuroglancer-layer-item-label';
    labelElement.appendChild(labelElementText);
    visibleProgress.className = 'neuroglancer-layer-item-visible-progress';
    prefetchProgress.className = 'neuroglancer-layer-item-prefetch-progress';
    layerNumberElement.className = 'neuroglancer-layer-item-number';
    valueElement.className = 'neuroglancer-layer-item-value';
    valueElement.style.display = 'none';
    const valueContainer = document.createElement('div');
    valueContainer.className = 'neuroglancer-layer-item-value-container';
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'neuroglancer-layer-item-button-container';
    // const visibleCheckbox = document.createElement('input');
    // visibleCheckbox.type= 'checkbox'
    const closeElement = makeCloseButton();
    closeElement.title = 'Remove layer from this layer group';
    closeElement.style.display = 'none';
    closeElement.addEventListener('click', (event: MouseEvent) => {
      if (this.panel.layerManager === this.panel.manager.rootLayers) {
        // The layer bar corresponds to a TopLevelLayerListSpecification.  That means there is just
        // a single layer group, archive the layer unconditionally.
        this.layer.setArchived(true);
      } else {
        // The layer bar corresponds to a LayerSubsetSpecification.  The layer is always contained
        // in the root LayerManager, as well as the LayerManager for each LayerSubsetSpecification.
        if (this.layer.containers.size > 2) {
          // Layer is contained in at least one other layer group, just remove it from this layer
          // group.
          this.panel.layerManager.removeManagedLayer(this.layer);
        } else {
          // Layer is not contained in any other layer group.  Archive it.
          this.layer.setArchived(true);
        }
      }
      event.stopPropagation();
    });
    const deleteElement = makeDeleteButton();
    deleteElement.title = 'Delete this layer';
    // deleteElement.style.display = this.layer.genename ? this.layer.genename.includes('ssDNA') || this.layer.genename.includes('template') ? 'none' : (this.layer.genename !== 'annotationCellbin') ? 'block' : 'none' : 'none';
    deleteElement.style.display = 'none'
    deleteElement.addEventListener('click', (event: MouseEvent) => {
      const _that = this;
      const layerName = _that.layer.genename
      deleteLayer(this.layer);
      let _json = viewer.state.toJSON();
      let _newlayers = _json.layers;
      if(document.getElementsByClassName(layerName)[0]){
        (document.getElementsByClassName(layerName)[0] as HTMLInputElement).checked = false;
      }
      event.stopPropagation();
      if(_newlayers.length === 1){
        _newlayers[0].visible = true;
      }
      _json.selectedLayer = {
        "visible": true,
        "layer": _newlayers[_newlayers.length - 1].genename
      }
      viewer.state.restoreState(_json);
    });
    // let win1:any;
    // let win2:any;
    // let winany:any;
    const protein_merge = makeIcon({
      svg: svg_proteins,
      title: i18n.layerPanelManyProtein,
      classList: ['protein-merge',`protein-merge-${layer.genename.replace(/ /g,'')}`],
      disabled: local_href_param.visible ? true : false,
      throttleClick: () => {
        let {width} = screen;
        let index: number = viewer.proteinsFilter.childWinArr?.length || 0;
        viewer.proteinsFilter.childWinArr?.push(window.open(`${window.location.href}&binSize=${viewer.binSize.value}&visible=${this.layer.genefile}&genelistFile=${this.layer.genefile}&layername=${this.layer.name}`,
          `childWin_${index}`,
          `height=600,width=900,top=${index * 50},left=${width - 900 - index * 50},resizable=no,location=yes,menubar=yes`));
      }
    });
    protein_merge.style.visibility = !layer.disabled && layer.type !== 'image' && !local_href_param.visible ? 'visible':'hidden';
    element.appendChild(protein_merge);
    element.style.paddingLeft = '1rem';
    element.appendChild(layerNumberElement);
    valueContainer.appendChild(valueElement);
    valueContainer.appendChild(buttonContainer);
    // valueContainer.appendChild(visibleCheckbox);
    listElement.classList.add('layer-name-control-layer-side');
    const visivleElement = this.registerDisposer(new LayerVisibilityWidget2(layer, isRadio, panel.manager.layerManager.managedLayers, groupLayerMap)).element;
    // visivleElement.style.display = this.layer.genetype === 'IFimage' ? 'none': 'auto';
    listElement.appendChild( visivleElement )
    listElement.style.visibility = this.layer.genename === 'annotationCellbin' ? 'hidden':'auto';
    buttonContainer.appendChild(closeElement);
    buttonContainer.appendChild(listElement);
    buttonContainer.appendChild(deleteElement);
    // element.appendChild(color);
    element.appendChild(valueContainer);
    element.appendChild(labelElement);
    const downIcon = document.createElement('div')
    downIcon.classList.add('down-icon')
    downIcon.innerHTML = svg_down
    element.appendChild(downIcon)
    const positionWidget = this.registerDisposer(new PositionWidget(
      layer.localPosition, layer.localCoordinateSpaceCombiner, {copyButton: false}));
    element.appendChild(positionWidget.element);
    positionWidget.element.addEventListener('click', (event: MouseEvent) => {
      event.stopPropagation();
    });
    positionWidget.element.addEventListener('dblclick', (event: MouseEvent) => {
      event.stopPropagation();
    });
    let selfClickFlag = false
    const {configStorage} = viewer
    const {activedTool} = configStorage
    activedTool.changed.add(() => {
      if (selfClickFlag) {
        selfClickFlag = false
        return
      }
      if (element.dataset.selected === 'true' && activedTool.value !== layer.name) {
        element.dataset.selected = 'false' // 切换配置时，取消上一个选中状态
      }
    })
    if(this.layer.name !== local_href_param.layername && local_href_param.visible)element.classList.add('ng-common-disabled')
    const clickHandle = (event: MouseEvent) => {
      // const {configStorage} = viewer
      // const {parentStorage} = configStorage
      if (!this.layer.visible) return
      selfClickFlag = true
      const elementSelected = element.dataset.selected === 'true';
      element.dataset.selected = elementSelected ? 'false' : 'true'
      if (!elementSelected) {
        configStorage.clear();
        configStorage.registParentSingle(layer.name, element) // 每次点击选中都注册，避免restoState的时候dom更新，配置窗位置异常
        configStorage.setActivedTool(layer.name)
        // viewer.updateGeneHeatMapFilter(true);
      } else if (activedTool.value === layer.name) {
        // activedTool.value = ''
        configStorage.setActivedTool(undefined);
        viewer.clearActiveTool();
      }
      // const a = document.getElementsByClassName('neuroglancer-layer-item-label').length;
      // for(let i = 0; i < a; i++){
      //   document.getElementsByClassName('neuroglancer-layer-item neuroglancer-noselect')[i].setAttribute('data-selected', 'false');
      // }

      if(layer.genename !== 'annotationCellbin')
        if(layer.genetype === 'splicePic'){
          let _json = viewer.state.toJSON();
          let _layers = _json.layers;
          _layers.forEach((item:any)=>{
            if(item.genetype === 'splicePic'){
              item.shader = `#uicontrol invlerp normalized
            void main() {
              emitGrayscale(normalized());
            }
            `;
              if(item.name === layer.name){
                item.source = {
                  "url": typeof item.source === 'object' ? item.source.url : item.source,
                  "subsources": {"default":true,"bounds":true},
                  "transform": item.source?.transform,
                  "enableDefaultSubsources": false
                };
                item.tab = "rendering";
              }else{
                item.source = {
                  "url": typeof item.source === 'object' ? item.source.url : item.source,
                  "subsources": {"default":true},
                  "transform": item.source?.transform,
                  "enableDefaultSubsources": false
                }
              }
            }
          });
          _json.selectedLayer = {
            "layer": layer.name,
            "visible": true
          }
          _json.layers = _layers;
          viewer.state.restoreState(_json);
        }else if(layer.genename.includes('template')){
          return;
        }else{
          panel.selectedLayer.toggle(layer);
          event.stopPropagation();
          event.preventDefault();
        }
    }
    labelElement.addEventListener('click', clickHandle);
    downIcon.addEventListener('click', clickHandle);

    element.addEventListener('contextmenu', (event: MouseEvent) => {
      // element.setAttribute('data-selected', 'true');
      panel.selectedLayer.layer = layer;
      panel.selectedLayer.visible = true;
      event.stopPropagation();
      event.preventDefault();
    });
    registerLayerDragHandlers(
      panel, element, layer, {getLayoutSpec: () => panel.getLayoutSpecForDrag()});
    registerLayerBarDropHandlers(this.panel, element, this.layer);
  }

  update() {
    const {layer, element} = this;
    // 图层layer名称不用做国际化转换
    const splitNameArray = layer.name.split('-');
    const showName = splitNameArray[1] || splitNameArray[0].replace(/_=_\d/,'');
    const heatmap_name = showName.includes('gene_heatmap') ? showName.replace(/_=_\d/,'').replace('gene_heatmap_layer','').replace('gene_heatmap','') : showName.includes('protein_heatmap') ? showName.replace(/_=_\d/,'').replace('protein_heatmap_layer','').replace('protein_heatmap','') : '';
    this.labelElementText.textContent = showName.includes('metabon_heatmap') ? i18n['metabon_heatmap_layer']+heatmap_name : showName.includes('gene_heatmap') ? i18n['gene_heatmap_layer']+heatmap_name : showName.includes('protein_heatmap') ? i18n['protein_heatmap_layer']+heatmap_name : getLayerName(i18n['layerPanel'+showName] || showName);
    this.labelElement.title = showName.includes('metabon_heatmap') ? i18n['metabon_heatmap_layer']+heatmap_name : showName.includes('gene_heatmap') ? i18n['gene_heatmap_layer']+heatmap_name : showName.includes('protein_heatmap') ? i18n['protein_heatmap_layer']+heatmap_name : getLayerName(i18n['layerPanel'+showName] || showName);
    if(showName.includes('cell_cluster_layer') && viewer.binSize.value === 'cellbin'){
      this.labelElementText.textContent = i18n.cell_cluster_layer;
      this.labelElement.title = i18n.cell_cluster_layer;
    }else if(showName.includes('cell_cluster_layer') && viewer.binSize.value !== 'cellbin'){
      this.labelElementText.textContent = i18n.bin_cluster_layer
      this.labelElement.title = i18n.bin_cluster_layer
    }else if(showName.includes('UMAP')){
      this.labelElementText.textContent = i18n.UMAP
      this.labelElement.title = i18n.UMAP
    }
    // this.labelElementText.textContent = i18n[layer.name] || layer.name;
    // this.labelElement.title = i18n[layer.name] || layer.name;
    // this.labelElementText.textContent = layer.name;
    // this.labelElement.title = layer.name;
    element.dataset.visible = layer.visible.toString();
    element.dataset.pick = layer.pickEnabled.toString();
    let title = `Click to ${layer.visible ? 'hide' : 'show'}`;
    element.title = title;
    if(layer.heatmap && layer.visible && !allToolsButton.genelistFile)allToolsButton.genelistFile = layer.genefile;
  }

  disposed() {
    // this.element.remove();
    super.disposed();
  }
}

// 二级图层
interface TwoLevelLayerWidgetOptions {
  name: string,
  level?: number,
  isRadio?: boolean
}
export class TwoLevelLayerWidget extends RefCounted {
  element = document.createElement('div');
  labelElement = document.createElement('div');
  childContainer = document.createElement('div');
  parentCheckbox: LayerVisibilityWidget3;
  nextChild: any;
  layerWidgets = new Map<ManagedUserLayer, LayerWidget>();
  disabled: boolean = true;
  private layerWidgetInsertionPoint = document.createElement('div');
  constructor(public panel: LayerBar, options: TwoLevelLayerWidgetOptions) {
    super();
    const {element, labelElement, childContainer} = this
    const {name, level} = options
    element.classList.add('neuroglancer-layer-parent', 'ng-common-disabled')
    this.parentCheckbox = this.registerDisposer(new LayerVisibilityWidget3(options.isRadio))
    const visivleElement = this.parentCheckbox.element;

    labelElement.appendChild( visivleElement )
    labelElement.classList.add('neuroglancer-layer-parent-label')
    if (level && level < 3) {
      labelElement.classList.add('neuroglancer-layer-parent-label-shadow')
    }
    const labelValue = document.createElement('span');
    console.log(name)
    labelValue.innerHTML = i18n[name] || name
    labelElement.appendChild(labelValue)

    const downIcon = document.createElement('div')
    downIcon.classList.add('down-icon')
    downIcon.innerHTML = svg_down
    labelElement.appendChild(downIcon)

    labelElement.dataset.checked = 'true'
    labelElement.addEventListener('click', () => {
      const isDrop = labelElement.dataset.checked === 'true'
      labelElement.dataset.checked = isDrop ? 'false': 'true';
      const nextSibling = (labelElement.nextSibling as HTMLElement)
      if (isDrop) {
        console.log('nextSibling---', nextSibling)
      }
      nextSibling.classList[isDrop ? 'add' : 'remove']('hidden')
    })

    element.appendChild(labelElement)
    element.appendChild(childContainer)
    if(options.name.includes('main_analysis_layer')){
      childContainer.classList.add('main_analysis_layer-scroll');
    }
    this.layerWidgetInsertionPoint.style.display = 'none';
    childContainer.appendChild(this.layerWidgetInsertionPoint)
  }
  handleDisabled () {
    this.disabled = false
    this.element.classList.remove('ng-common-disabled')
  }
  appendLayerWidget (layer: ManagedUserLayer, layerWidget: LayerWidget) {
    if (!this.nextChild) this.nextChild = this.layerWidgetInsertionPoint.nextElementSibling;
    if (layerWidget.element !== this.nextChild) {
      if (this.disabled) this.handleDisabled()
      layerWidget.element.classList.add('neuroglancer-layer-child')
      // this.childContainer.insertBefore(layerWidget.element, this.nextChild);
      this.childContainer.appendChild(layerWidget.element);
      this.layerWidgets.set(layer, layerWidget);
      this.parentCheckbox.addLayer(layer)
    }
    this.nextChild = layerWidget.element.nextElementSibling;
  }
}

export class LayerBar extends RefCounted {
  layerWidgets = new Map<ManagedUserLayer, LayerWidget>();
  parentMap = new Map<string, TwoLevelLayerWidget|HTMLElement>();
  element = document.createElement('div');
  private layerUpdateNeeded = true;
  private valueUpdateNeeded = false;
  dropZone: HTMLDivElement;
  tootips = new Tooltip();
  private layerWidgetInsertionPoint = document.createElement('div');
  private positionx: number;
  private positiony: number;
  private positionWidget = this.registerDisposer(new PositionWidget(
    this.viewerNavigationState.position.value, this.manager.root.coordinateSpaceCombiner));
  /**
   * For use within this module only.
   */
  dropLayers: DropLayers|undefined;

  dragEnterCount = 0;

  get layerManager() {
    return this.manager.layerManager;
  }

  constructor(
    public display: DisplayContext, public manager: LayerListSpecification,
    public viewerNavigationState: LinkedViewerNavigationState,
    public selectedLayer: Owned<SelectedLayerState>, public getLayoutSpecForDrag: () => any,
    public showLayerHoverValues: WatchableValueInterface<boolean>) {
    super();
    this.registerDisposer(selectedLayer);
    const {element} = this;
    element.className = 'neuroglancer-layer-panel';
    this.registerDisposer(manager.layerSelectedValues.changed.add(() => {
      this.handleLayerValuesChanged();
    }));
    this.registerDisposer(manager.layerManager.layersChanged.add(() => {
      this.handleLayersChanged();
    }));
    this.registerDisposer(selectedLayer.changed.add(() => {
      this.handleLayersChanged();
    }));
    this.registerDisposer(showLayerHoverValues.changed.add(() => {
      this.handleLayerItemValueChanged();
    }));
    this.element.dataset.showHoverValues = this.showLayerHoverValues.value.toString();
    this.layerWidgetInsertionPoint.style.display = 'none';
    this.element.appendChild(this.layerWidgetInsertionPoint);

    let addButton = makeIcon({
      svg: svg_plus,
      title: 'Click to add layer, control+click/right click/⌘+click to add local annotation layer.',
    });
    addButton.classList.add('neuroglancer-layer-add-button');
    addButton.style.display = 'none';
    let dropZone = this.dropZone = document.createElement('div');
    dropZone.className = 'neuroglancer-layer-panel-drop-zone';

    const addLayer = (event: MouseEvent) => {
      if (event.ctrlKey || event.metaKey || event.type === 'contextmenu') {
        const layer = makeLayer(
          this.manager, 'annotation', {type: 'annotation', 'source': 'local://annotations'});
        this.manager.add(layer);
        this.selectedLayer.layer = layer;
        this.selectedLayer.visible = true;
      } else {
        this.addLayerMenu();
      }
    };
    this.registerEventListener(addButton, 'click', addLayer);
    this.registerEventListener(addButton, 'contextmenu', addLayer);
    element.appendChild(addButton);
    element.appendChild(dropZone);
    this.registerDisposer(preventDrag(addButton));

    element.appendChild(this.positionWidget.element);
    const updatePositionWidgetVisibility = () => {
      const linkValue = this.viewerNavigationState.position.link.value;
      this.positionWidget.element.style.display =
        linkValue === NavigationLinkType.LINKED ? 'none' : '';
    };
    this.registerDisposer(
      this.viewerNavigationState.position.link.changed.add(updatePositionWidgetVisibility));
    updatePositionWidgetVisibility();

    this.update();
    this.updateChunkStatistics();

    registerLayerBarDragLeaveHandler(this);
    registerLayerBarDropHandlers(this, dropZone, undefined);

    // Ensure layer widgets are updated before WebGL drawing starts; we don't want the layout to
    // change after WebGL drawing or we will get flicker.
    // this.registerDisposer(display.updateStarted.add(() => this.updateLayers()));

    this.registerDisposer(manager.chunkManager.layerChunkStatisticsUpdated.add(
      this.registerCancellable(animationFrameDebounce(() => this.updateChunkStatistics()))));
  }

  disposed() {
    this.layerWidgets.forEach(x => x.dispose());
    this.layerWidgets = <any>undefined;
    removeFromParent(this.element);
    removeFromParent(this.tootips.element);
    super.disposed();
  }

  handleLayersChanged() {
    this.layerUpdateNeeded = true;
    this.handleLayerValuesChanged();
  }

  handleLayerValuesChanged() {
    if (!this.valueUpdateNeeded) {
      this.valueUpdateNeeded = true;
      this.scheduleUpdate();
    }
  }

  handleLayerItemValueChanged() {
    this.element.dataset.showHoverValues = this.showLayerHoverValues.value.toString()
  }
  private scheduleUpdate = this.registerCancellable(animationFrameDebounce(() => this.update()));
  sortFieldMatch (field:string) {
    let fields = field.includes('ID') ? 'id': '';
    const stringArray = field.replace('ID','').split('') // 将字符串分割成相应的字符串数组
    let newField = field.replace('ID','');
    stringArray.forEach(t => {
      if (/[A-Z]/.test(t)) {
        // 遍历分割之后的字符串组，将找到的大写字母替换成其他字符串，将替换后的字符串赋值给另外一个新的string 变量
        newField = newField.replace(t, ` ${t.toUpperCase()}`)
      }
    })
    return newField + fields;
  }
  update() {
    this.valueUpdateNeeded = false;
    this.updateLayers();
    if (this.showLayerHoverValues.value === false) {
      return
    }
    let values = this.manager.layerSelectedValues;
    for (let [layer, widget] of this.layerWidgets) {
      let userLayer = layer.layer;
      let text = '';
      let propertyStr = '';
      if (userLayer !== null) {
        let state = values.get(userLayer);
        const {showHoverInfo: {value: showHoverInfo}} = viewer
        if (state !== undefined) {
          // console.log(this.display, values, userLayer, this)     // state.value中可以读到cluster的label值, state.annotationSubsource：标记类型，state.annotationId标记id
          // if(state.annotationSubsource === 'sign' && state.annotationId !== undefined){
            // let staticAnnotations = (layer.layer!.dataSources[0].loadState_ as LoadedLayerDataSource)?.subsources[2].subsourceEntry.subsource!.staticAnnotations;
            // const dom = document.getElementById('neuroglancer-container');
            // dom?.addEventListener('contextmenu',()=>{
            //   let reference = staticAnnotations?.getReference(state!.annotationId!);
            //   let referenceValue:any = {};  //  staticAnnotations!.references为map对象，需存储references
            //   for(let value of staticAnnotations!.references){
            //     referenceValue[ value[0] ] = value[1];
            //   };
            //   if(state?.annotationId?.includes('line')){
            //     for(let i in referenceValue){
            //       if(i.includes(state!.annotationId!)){
            //         staticAnnotations?.delete(referenceValue[i]);
            //       }
            //     }
            //   }
            //   staticAnnotations?.delete(reference as AnnotationReference );
            // });
          // }else
          if (showHoverInfo){
            const {value} = state;
            const {property} = state;
            if (value !== undefined) {
              text = '' + value;
            }
            if(property !== undefined && property.length && state.annotationId !== undefined){
              const isUmapLayer = userLayer.managedLayer.name.includes('UMAP');
              if (isUmapLayer) {
                let cluster = -1,posX,posY;
                property.forEach((item)=>{
                  switch (item.name){
                    case 'c':
                      cluster = item.counts;
                      break;
                    case 'category_x':
                      posX = Math.floor(item.counts);
                      break;
                    case 'category_y':
                      posY = Math.floor(item.counts);
                      break;
                  }
                })
                propertyStr += `<div><span>${ i18n['c'] }：</span><span>${ thousandth_formatter(cluster) }</span></div>`
                this.tootips.element.innerHTML = `<div><span>${i18n.location}: </span><span>(${posX}, ${posY})</span></div>${propertyStr}`;
                if(Object.keys(viewer.saveSelectedKeyVal).length > 0)this.tootips.element.innerHTML+=`<div style="color: #999999;font-size: 0.75rem">${i18n.more_details}</div>`
              } else {
                property.forEach((item)=>{
                  if(!item.name.includes('dnb') && !item.name.includes('cellType')){
                    if(allToolsButton.genelistName?.includes('protein') && item.name.includes('geneCount'))item.name = 'proteinCount'
                    if(allToolsButton.genelistName?.includes('metabon') && item.name === "midCount") {
                      propertyStr += `<div><span>${ i18n.intensity }：</span><span>${ thousandth_formatter(item.counts) }</span></div>`
                    } else if (allToolsButton.genelistName?.includes('metabon') && item.name === "geneCount") {
                      propertyStr += `<div><span>${ i18n.metabon_type }：</span><span>${ thousandth_formatter(item.counts) }</span></div>`
                    } else {
                      propertyStr += `<div><span>${ i18n[item.name] }：</span><span>${ thousandth_formatter(item.counts) }</span></div>`
                    }
                  }
                })

              this.positionx = values.mouseState.position[0];
              this.positiony = values.mouseState.position[1];
              if(userLayer.dataSources[0].spec_.transform){
                const newTransform = mat4.fromValues(0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0);
                let layerTransform:any = [];
                (<any>viewer).layerManager.managedLayers.forEach((item: any, index: number)=>{
                  if(item.visible){
                    layerTransform = (<any>viewer).layerManager.managedLayers[index].layer_?.dataSources[0]?.loadState_!.transform!.value_.transform as any;
                    if(layerTransform.length < 16){
                      layerTransform = [layerTransform[0], layerTransform[1], layerTransform[2], 0,
                        layerTransform[3], layerTransform[4], layerTransform[5], 0,
                        layerTransform[6], layerTransform[7], layerTransform[8], 0,
                        0, 0, 0, 1]
                    }
                  }
                })
                // const layerTransform = (<any>viewer).layerManager.managedLayers[0].layer_?.dataSources[0]?.loadState_!.transform!.value_.transform as any;
                mat4.invert(newTransform, layerTransform)
                let coor = vec4.fromValues(values.mouseState.position[0], values.mouseState.position[1], 1, 1);
                vec4.transformMat4(coor, coor, newTransform);
                this.positionx = Math.floor( coor[0] );
                this.positiony = Math.floor( coor[1] )
              } else {
                this.positionx = Math.floor( this.positionx );
                this.positiony = Math.floor( this.positiony );
                }
                this.tootips.element.innerHTML = `<div><span>${i18n.location}: </span><span>(${this.positionx}, ${this.positiony})</span></div>${propertyStr}`;
                if(Object.keys(viewer.saveSelectedKeyVal).length > 0)this.tootips.element.innerHTML+=`<div style="color: #999999;font-size: 0.75rem">${i18n.more_details}</div>`
              }
            }else{
              propertyStr = ''
            }
          }
        }
      }
      if (text === widget.prevValueText) continue;
      widget.prevValueText = text;
      if (text.length > widget.maxLength) {
        const length = widget.maxLength = text.length;
        widget.valueElement.style.width = `${length}ch`;
      }
      widget.valueElement.textContent = text;
      if(text){
        if(document.getElementsByClassName('lassoBtn')[0] && document.getElementsByClassName('lassoBtn')[0].getAttribute('isclicked') === 'true'){
          propertyStr = '';
        }
        if(values.mouseState.active && propertyStr){
          const tootipsX = window.innerWidth - values.mouseState.pageX < this.tootips.element.offsetWidth ? values.mouseState.pageX - this.tootips.element.offsetWidth : values.mouseState.pageX;
          const tootopsY = window.innerHeight - values.mouseState.pageY < this.tootips.element.offsetHeight ? values.mouseState.pageY - this.tootips.element.offsetHeight : values.mouseState.pageY;
          this.tootips.updatePosition(tootipsX, tootopsY);
        }
      }else{
        this.tootips.element.style.visibility = 'hidden';
      }
    }
  }

  private updateChunkStatistics() {
    for (const [layer, widget] of this.layerWidgets) {
      let numVisibleChunksNeeded = 0;
      let numVisibleChunksAvailable = 0;
      let numPrefetchChunksNeeded = 0;
      let numPrefetchChunksAvailable = 0;
      const userLayer = layer.layer;
      if (userLayer !== null) {
        for (const {layerChunkProgressInfo} of userLayer.renderLayers) {
          numVisibleChunksNeeded += layerChunkProgressInfo.numVisibleChunksNeeded;
          numVisibleChunksAvailable += layerChunkProgressInfo.numVisibleChunksAvailable;
          numPrefetchChunksNeeded += layerChunkProgressInfo.numPrefetchChunksNeeded;
          numPrefetchChunksAvailable += layerChunkProgressInfo.numPrefetchChunksAvailable;
        }
      }
      widget.visibleProgress.style.width =
        `${numVisibleChunksAvailable / Math.max(1, numVisibleChunksNeeded) * 100}%`;
      widget.prefetchProgress.style.width =
        `${numPrefetchChunksAvailable / Math.max(1, numPrefetchChunksNeeded) * 100}%`;
    }
  }

  updateLayers() {
    if (!this.layerUpdateNeeded) {
      return;
    }
    this.layerUpdateNeeded = false;
    let container = this.element;
    let layers = new Set();
    let nextChild = this.layerWidgetInsertionPoint.nextElementSibling;
    this.manager.rootLayers.updateNonArchivedLayerIndices();
    if (viewer !== undefined && this.manager.layerManager.managedLayers.length) {
      const {layerTree, parentMap, groupLayerMap} = viewer
      const {layerMap, hoverLayerMap} = this.manager.layerManager;
      if(layerTree){
        for (let i = 0; i < layerTree.length; i++) {
          const layerItem = layerTree[i]
          let layerLevel = layerItem.depth || 2;
          let layerType = layerItem.name === 'main_analysis_layer';   //  判定是否为主分析图层
          let layer_main_group:any = new Map();
          if (layerLevel > 2) {
            const image_layer_container = document.createElement('div');
            image_layer_container.classList.add('image_layer-scroll') // 添加image layer滚动
            const label = document.createElement('div')
            if (!parentMap.get(layerItem.name)) {
              label.classList.add('wawa')
              image_layer_container.classList.add(layerItem.name);//用className标记图像控制
              label.innerText = i18n[layerItem.name] || layerItem.name
              label.classList.add('neuroglancer-layer-parent-label-shadow');
            }
            for (let k = 0; k < layerItem.children.length; k++) {
              const childLayer = layerItem.children[k]
              if (childLayer.type || (childLayer.has_data && JSON.stringify(childLayer.has_data) !== '{}')) {
                const layer = layerMap.get(childLayer.name)
                if (!layer) continue;
                if (layer.archived && !this.dropLayers?.layers.has(layer)) continue;
                layers.add(layer);
                let widget = this.layerWidgets.get(layer);
                const layerIndex = layer.nonArchivedLayerIndex;
                if (widget === undefined) {
                  widget = new LayerWidget(groupLayerMap, layer, this);
                  this.layerWidgets.set(layer, widget);
                  groupLayerMap.set(layer.name, layer)
                }
                widget.layerNumberElement.textContent = '' + (1 + layerIndex);
                widget.update();
                let {element} = widget;
                if (element !== nextChild) {
                  // container.insertBefore(widget.element, nextChild);
                  image_layer_container.appendChild(widget.element)
                }
                nextChild = element.nextElementSibling;
              } else {
                let layerGroup:any = parentMap.get(childLayer.name) || this.parentMap.get(childLayer.name);
                if (!layerGroup) {
                  layerGroup = new TwoLevelLayerWidget(this, {name: childLayer.name, isRadio: !childLayer.is_multi_select, level: layerLevel})
                  if(layerType)layer_main_group.set(childLayer.name, layerGroup);
                  parentMap.set(childLayer.name, layerGroup)
                  this.parentMap.set(childLayer.name, layerGroup)
                  image_layer_container.appendChild(layerGroup.element)
                }
                for (let j =0; j < childLayer.children.length; j++) {
                  const layer = layerMap.get(childLayer.children[j].name)
                  if (!layer) continue;
                  if (layer.archived && !this.dropLayers?.layers.has(layer)) continue;
                  layers.add(layer);
                  let widget = this.layerWidgets.get(layer);
                  const layerIndex = layer.nonArchivedLayerIndex;
                  if (widget === undefined) {
                    widget = new LayerWidget(groupLayerMap, layer, this, !childLayer.is_multi_select);
                    this.layerWidgets.set(layer, widget);
                    groupLayerMap.set(layer.name, layer)
                  }
                  widget.layerNumberElement.textContent = '' + (1 + layerIndex);
                  widget.update();
                  // let {element} = widget;
                  // if (element !== nextChild) {
                  //   layerGroup?.appendLayerWidget(layer, widget)
                  // }
                  // layerGroup?.appendLayerWidget(layer, widget)
                  if(container && (container as HTMLElement).getElementsByClassName(layer.name)[0])continue;
                  if(layerType && layer_main_group.get(childLayer.name)){
                    for(let m of layer_main_group){
                      if (!m[1].layerWidgets.get(layer)) {
                        m[1]?.appendLayerWidget(layer, widget)
                      }
                    }
                  }else{
                    if (!layerGroup.layerWidgets.get(layer)) {
                    layerGroup?.appendLayerWidget(layer, widget)
                    }
                  }
                  // nextChild = element.nextElementSibling;
                }
              }
            }
            if(image_layer_container.children.length > 0 && !parentMap.get(layerItem.name)){
              container.appendChild(label)
              parentMap.set(layerItem.name, label);
              container.appendChild(image_layer_container);
            }
            // container.removeChild(image_layer_container)
          } else {
            if (layerItem.type || (layerItem.has_data && JSON.stringify(layerItem.has_data) !== '{}')) {
              const layer = layerMap.get(layerItem.name)
              if (!layer) continue;
              if (layer.archived && !this.dropLayers?.layers.has(layer)) continue;
              layers.add(layer);
              let widget = this.layerWidgets.get(layer);
              const layerIndex = layer.nonArchivedLayerIndex;
              if (widget === undefined) {
                widget = new LayerWidget(groupLayerMap, layer, this);
                this.layerWidgets.set(layer, widget);
                groupLayerMap.set(layer.name, layer)
              }
              widget.layerNumberElement.textContent = '' + (1 + layerIndex);
              widget.update();
              if(container && container.getElementsByClassName(layer.name)[0])continue;
              let {element} = widget;
              if (element !== nextChild) {
                // container.insertBefore(widget.element, nextChild);
                console.log(container.children.item )
                container.appendChild(widget.element)
              }
              nextChild = element.nextElementSibling;
            } else {
              let layerGroup:any = parentMap.get(layerItem.name) || this.parentMap.get(layerItem.name);
              if (!layerGroup) {
                layerGroup = new TwoLevelLayerWidget(this, {name: layerItem.name, isRadio: !layerItem.is_multi_select, level: layerLevel})
                parentMap.set(layerItem.name, layerGroup)
                this.parentMap.set(layerItem.name, layerGroup)
                console.log(container.children.item )
                container.appendChild(layerGroup.element)
              }
              if (layerItem.children) {
                for (let j =0; j < layerItem.children.length; j++) {
                  const layer = layerMap.get(layerItem.children[j].name)
                  if (!layer) continue;
                  if (layer.archived && !this.dropLayers?.layers.has(layer)) continue;
                  layers.add(layer);
                  let widget = this.layerWidgets.get(layer);
                  const layerIndex = layer.nonArchivedLayerIndex;
                  if (widget === undefined) {
                    widget = new LayerWidget(groupLayerMap, layer, this, !layerItem.is_multi_select);
                    this.layerWidgets.set(layer, widget);
                    groupLayerMap.set(layer.name, layer)
                  }
                  widget.layerNumberElement.textContent = '' + (1 + layerIndex);
                  widget.update();
                  // let {element} = widget;
                  // if (element !== nextChild) {
                  //   layerGroup?.appendLayerWidget(layer, widget)
                  // }
                  if(container && (container as HTMLElement).getElementsByClassName(layer.name)[0])continue;
                  if (!layerGroup.layerWidgets.get(layer)) {
                    layerGroup?.appendLayerWidget(layer, widget)
                  }
                }
              }
            }
          }
        }
        for (let [, layer] of hoverLayerMap) {
          if (layer.archived && !this.dropLayers?.layers.has(layer)) continue;
          layers.add(layer);
          let widget = this.layerWidgets.get(layer);
          // const layerIndex = layer.nonArchivedLayerIndex;
          if (widget === undefined) {
            widget = new LayerWidget(groupLayerMap, layer, this);
            widget.element.style.display = 'none'
            this.layerWidgets.set(layer, widget);
          }
        }
      }
      // for (const layer of this.manager.layerManager.managedLayers) {
      //   if (layer.archived && !this.dropLayers?.layers.has(layer)) continue;
      //   layers.add(layer);
      //   let widget = this.layerWidgets.get(layer);
      //   const layerIndex = layer.nonArchivedLayerIndex;
      //   if (widget === undefined) {
      //     if (!layerGroup) {
      //       layerGroup = new TwoLevelLayerWidget(this)
      //       container.appendChild(layerGroup.element)
      //     }
      //     widget = new LayerWidget(layer, this, true);
      //     this.layerWidgets.set(layer, widget);
      //   }
      //   widget.layerNumberElement.textContent = '' + (1 + layerIndex);
      //   widget.update();
      //   let {element} = widget;
      //   if (element !== nextChild) {
      //     // container.insertBefore(widget.element, nextChild);
      //     layerGroup?.appendLayerWidget(layer, widget)
      //   }
      //   nextChild = element.nextElementSibling;
      // }
    }
    for (let [layer, widget] of this.layerWidgets) {
      if (!layers.has(layer)) {
        this.layerWidgets.delete(layer);
        widget.dispose();
      }
    }
  }

  addLayerMenu() {
    addNewLayer(this.manager, this.selectedLayer);
  }
}

function getLayerName(name: string){
  if(name.toLocaleLowerCase().includes('tissuemask')){
    return name.replace('TissueMask', i18n.layerPanelTissueMask)
  }else if(name.toLocaleLowerCase().includes('cellmask')){
    return name.replace('CellMask', i18n.layerPanelCellMask)
  }else {
    return name.replace('Image', i18n.layerPanelImage)
  }
}